import React from 'react';

import styled from 'styled-components';

import { QuoteBlock } from 'components/lib/QuoteBlock';
import { SalesPlaybookCategory } from 'types/recommendations';

import { SALES_PLAYBOOK_BADGE_LABEL, SALES_PLAYBOOK_STATUS, SALES_PLAYBOOK_VARIANT_CLASS } from './constants';
import SalesPlaybookQuestionList from './SalesPlaybookQuestionList';
import { getSalesTalkTrackCategoryId } from './utils';

const CardContainer = styled.div`
  border-radius: 10px;
  border: solid 1px var(--line-gray);
  background-color: var(--white);
  display: flex;
  flex-direction: row;
  overflow: hidden;
  min-width: 0;
  scroll-margin-top: 24px;
`;

const Indicator = styled.div`
  width: 5px;
  min-width: 5px;

  &.lead-with {
    background-image: linear-gradient(to bottom, var(--mint-green), var(--positive-green));
  }

  &.position-against {
    background-image: linear-gradient(to bottom, var(--lavender-purple), var(--competitor-purple));
  }

  &.handle-objections {
    background-image: linear-gradient(to bottom, var(--amber-light), var(--inactive-orange));
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 24px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const CardTitle = styled.div`
  font-size: 16px;
  font-family: var(--font-bold);
`;

const StatusBadge = styled.span`
  font-size: 12px;
  font-family: var(--font-regular);
  padding: 6px 10px;
  border-radius: 4px;
  white-space: nowrap;

  &.lead-with {
    background-color: var(--light-green);
    color: var(--positive-green);
  }

  &.position-against {
    background-color: var(--lavender-soft);
    color: var(--competitor-purple);
  }

  &.handle-objections {
    background-color: #fff5e6;
    color: var(--inactive-orange);
  }
`;

const SectionLabel = styled.div`
  font-size: 12px;
  font-family: var(--font-bold);
  color: var(--text-primary);
  margin-top: 16px;
  margin-bottom: 6px;
`;

const SectionText = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.5;
`;

type Props = {
  category: SalesPlaybookCategory;
};

const SalesPlaybookCard = ({ category }: Props) => {
  const variantClass = SALES_PLAYBOOK_VARIANT_CLASS[category.status];
  const badgeLabel = SALES_PLAYBOOK_BADGE_LABEL[category.status];

  return (
    <CardContainer id={getSalesTalkTrackCategoryId(category.title)}>
      <Indicator className={variantClass} />
      <Content>
        <CardHeader>
          <CardTitle>{category.title}</CardTitle>
          <StatusBadge className={variantClass}>{badgeLabel}</StatusBadge>
        </CardHeader>
        <SectionLabel>What families ask:</SectionLabel>
        <SalesPlaybookQuestionList questions={category.what_families_ask} />
        <SectionLabel>Your position:</SectionLabel>
        <SectionText>{category.your_position}</SectionText>
        <SectionLabel>What to say:</SectionLabel>
        <QuoteBlock>{category.what_to_say}</QuoteBlock>
        {category.status !== SALES_PLAYBOOK_STATUS.LEAD_WITH && category.if_they_push_back && (
          <>
            <SectionLabel>If they push back:</SectionLabel>
            <QuoteBlock>{category.if_they_push_back}</QuoteBlock>
          </>
        )}
      </Content>
    </CardContainer>
  );
};

export default SalesPlaybookCard;
